import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify"; // ✅ Import Toastify
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners"; 
import _ from "lodash";

import CreateQuotationForm from "../components/CreateQuotationForm";
import ItemsForm from "../components/ItemsForm";
import QuotationPreview from "../components/QuotationPreview";
import { pdf } from "@react-pdf/renderer"; 
import bankAccounts from "../data/bankAccounts.json"; 
import { apiURL } from "../config/config";

// ✅ ฟังก์ชันปัดเศษทศนิยม 2 ตำแหน่ง (ปัดขึ้นถ้าหลักที่ 3 >= 5)
const roundUp = (num) => {
  return (num * 100) % 1 >= 0.5 ? _.ceil(num, 2) : _.round(num, 2);
};

const CreateQuotation = () => {
  const navigate = useNavigate();

  const [quotationData, setQuotationData] = useState({
    title: "",
    client: "",
    clientId: "",
    salePerson: "",
    documentDate: "",
    productName: "",
    projectName: "",
    period: "",
    startDate: "",
    endDate: "",
    createBy: "",
    proposedBy: "",
    type: "M",
    fee: 0,
    items: [],
    discount: 0,
    CreditTerm : 30,
    createdByUser: "",
    totalBeforeFee: 0,
    netAmount: 0,
    remark: "",
  });

  const [item, setItem] = useState({
    description: "",
    unit: 1,
    unitPrice: "",
  });
  const [isDetailedForm, setIsDetailedForm] = useState(false); // ✅ Default เป็น Standard Form
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user?.username) {
      setQuotationData((prev) => ({
        ...prev,
        createdByUser: user.username,
      }));
    }
  }, []);

  useEffect(() => {
    const totalBeforeFee = quotationData.items.reduce(
      (sum, itm) => sum + itm.unit * itm.unitPrice,
      0
    );
    const netAmount =
      totalBeforeFee - quotationData.discount + totalBeforeFee * (quotationData.fee / 100);
    setQuotationData((prev) => ({ ...prev, totalBeforeFee, netAmount }));
  }, [quotationData.items, quotationData.discount, quotationData.fee]);

  const handleItemChange = (field, value) => {
    setItem((prev) => ({ ...prev, [field]: value }));
  };

  const addItem = () => {
    if (!item.description || !item.unit || !item.unitPrice) {
      toast.error("กรุณากรอกข้อมูลให้ครบทุกช่อง ❌");
      return;
    }
    setQuotationData((prev) => ({
      ...prev,
      items: [...prev.items, item],
    }));
    setItem({ description: "", unit: 1, unitPrice: "" });
  };

  const removeItem = (index) => {
    setQuotationData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateItem = (index, updatedItem) => {
    setQuotationData((prev) => ({
      ...prev,
      items: prev.items.map((itm, i) => (i === index ? updatedItem : itm)),
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuotationData((prev) => ({
      ...prev,
      [name]: name === "discount" || name === "fee" ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true); // ✅ เริ่ม loading

    if (quotationData.items.length === 0) {
      toast.error("กรุณาเพิ่มรายการสินค้าอย่างน้อย 1 รายการ");
      setIsLoading(false);
      return;
    }

    const processedItems = quotationData.items.map((item) => ({
      ...item,
      unitPrice: roundUp(item.unitPrice),
      amount: roundUp(item.unit * roundUp(item.unitPrice)),
    }));

    const totalBeforeFee = roundUp(processedItems.reduce((sum, itm) => sum + itm.amount, 0));
    const calFee = roundUp(totalBeforeFee * (quotationData.fee / 100));
    const total = roundUp(totalBeforeFee + calFee);
    const amountBeforeTax = roundUp(total - quotationData.discount);
    const vat = roundUp(amountBeforeTax * 0.07);
    const netAmount = roundUp(amountBeforeTax + vat);

    const formattedData = {
      ...quotationData,
      totalBeforeFee,
      fee: roundUp(quotationData.fee),
      calFee,
      total,
      discount: roundUp(quotationData.discount),
      amountBeforeTax,
      vat,
      netAmount,
      items: processedItems,
      isDetailedForm,
    };

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${apiURL}quotations`, formattedData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("สร้างใบเสนอราคาสำเร็จ!");
      const quotationId = response.data._id;
      navigate(`/request-approve-flow/${quotationId}`);
    } catch (error) {
      console.error("Error creating quotation:", error);
      toast.error("ไม่สามารถสร้างใบเสนอราคาได้ ❌");
    } finally {
      setIsLoading(false); // ✅ หยุด loading
    }
  };

  const handlePreview = async () => {
    setIsLoading(true); // ✅ เริ่ม loading
    try {
      if (!quotationData.clientId) {
        toast.error("กรุณาเลือก Client ก่อน Preview ❌");
        setIsLoading(false);
        return;
      }

      const user = JSON.parse(localStorage.getItem("user")) || {};
      const company = user.company || "";
      const bankInfo = bankAccounts.companies[company] || {};

      const totalBeforeFee = quotationData.items.reduce(
        (sum, itm) => sum + itm.unit * itm.unitPrice,
        0
      );
      const feeAmount = totalBeforeFee * (quotationData.fee / 100);
      const totalAfterFee = totalBeforeFee + feeAmount;
      const amountBeforeTax = totalAfterFee - quotationData.discount;
      const vat = amountBeforeTax * 0.07;
      const netAmount = amountBeforeTax + vat;

      const clientResponse = await axios.get(`${apiURL}clients/${quotationData.clientId}`);
      const clientDetails = clientResponse.data;

      const updatedQuotationData = {
        ...quotationData,
        totalBeforeFee,
        feeAmount,
        total: totalAfterFee,
        amountBeforeTax,
        vat,
        netAmount,
        clientDetails,
      };

      const blob = await pdf(
        <QuotationPreview quotationData={updatedQuotationData} bankInfo={bankInfo} isDetailedForm={isDetailedForm}/>
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const newTab = window.open();
      if (newTab) {
        newTab.document.write(
          `<iframe src="${url}" style="width:100%;height:100%;border:none;"></iframe>`
        );
      }
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("ไม่สามารถสร้างตัวอย่าง PDF ได้ ❌");
    } finally {
      setIsLoading(false); // ✅ หยุด loading
    }
  };

  const handleClientChange = (clientId, clientName) => {
    setQuotationData((prev) => ({
      ...prev,
      clientId,
      client: clientName,
    }));
  };

  return (
    <div className="p-6 relative">
      {isLoading && (
        <div className="fixed inset-0 bg-white bg-opacity-70 z-50 flex justify-center items-center">
          <ClipLoader size={60} color="#6366F1" />
        </div>
      )}
      
      <h1 className="text-2xl font-bold mb-4">Create Quotation</h1>
      <form onSubmit={handleSubmit} className="bg-white shadow rounded p-6">
        <CreateQuotationForm
          quotationData={quotationData}
          handleChange={handleChange}
          setQuotationData={setQuotationData}
          handleClientChange={handleClientChange}
        />
        <ItemsForm
          items={quotationData.items}
          item={item}
          handleItemChange={handleItemChange}
          addItem={addItem}
          removeItem={removeItem}
          updateItem={updateItem}
          isDetailedForm={isDetailedForm}
          setIsDetailedForm={setIsDetailedForm}
        />
        <div className="flex justify-end mt-4 space-x-4">
          <button
            type="button"
            onClick={handlePreview}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            disabled={isLoading}
          >
            Preview
          </button>
          <button
            type="submit"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            disabled={isLoading}
          >
            Save Quotation
          </button>
        </div>
      </form>
    </div>
  );
};
export default CreateQuotation;
